import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, ShoppingBasket } from "lucide-react";

const MEALS = ["breakfast", "lunch", "dinner", "snack"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOf(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  return dt;
}
const fmt = (d) => d.toISOString().slice(0, 10);

export default function ClientPlanner() {
  const [weekStart, setWeekStart] = useState(() => fmt(mondayOf(new Date())));
  const [plan, setPlan] = useState({ entries: {}, checked: [] });
  const [recipes, setRecipes] = useState([]);
  const [picker, setPicker] = useState(null); // {date, meal}

  const days = useMemo(() => {
    const start = new Date(weekStart + "T00:00:00");
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return fmt(d);
    });
  }, [weekStart]);

  const recipeById = useMemo(() => Object.fromEntries(recipes.map((r) => [r.recipe_id, r])), [recipes]);

  const loadPlan = (ws) => api.get("/meal-plan", { params: { week_start: ws } })
    .then((r) => setPlan({ entries: r.data.entries || {}, checked: r.data.checked || [] }));

  useEffect(() => { api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadPlan(weekStart).catch(() => {}); }, [weekStart]);

  const shiftWeek = (n) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + n * 7);
    setWeekStart(fmt(d));
  };

  const setEntry = async (date, meal, recipeId) => {
    await api.put("/meal-plan/entry", { week_start: weekStart, date, meal_type: meal, recipe_id: recipeId });
    setPlan((p) => {
      const entries = { ...p.entries, [date]: { ...(p.entries[date] || {}) } };
      if (recipeId) entries[date][meal] = recipeId;
      else delete entries[date][meal];
      return { ...p, entries };
    });
    if (recipeId) toast.success("Added to your week");
  };

  const grocery = useMemo(() => {
    const counts = {};
    Object.values(plan.entries).forEach((meals) => {
      Object.values(meals).forEach((rid) => {
        const r = recipeById[rid];
        (r?.ingredients || []).forEach((ing) => { counts[ing] = (counts[ing] || 0) + 1; });
      });
    });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
  }, [plan.entries, recipeById]);

  const toggleCheck = async (item, checked) => {
    setPlan((p) => ({ ...p, checked: checked ? [...p.checked, item] : p.checked.filter((i) => i !== item) }));
    await api.put("/meal-plan/check", { week_start: weekStart, item, checked });
  };

  const weekLabel = `${new Date(days[0] + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(days[6] + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-planner">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Plan the week, skip the guesswork</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">Meal planner</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-ghost" onClick={() => shiftWeek(-1)} data-testid="planner-prev-week"><ChevronLeft size={16} /></button>
          <span className="font-label text-xs tracking-[0.2em] uppercase" data-testid="planner-week-label">{weekLabel}</span>
          <button className="btn-ghost" onClick={() => shiftWeek(1)} data-testid="planner-next-week"><ChevronRight size={16} /></button>
        </div>
      </header>

      <div className="overflow-x-auto pb-2">
        <div className="grid gap-px min-w-[900px]" style={{ gridTemplateColumns: "90px repeat(7, 1fr)", background: "var(--border-strong)" }}>
          <div style={{ background: "var(--surface-1)" }} />
          {days.map((d, i) => (
            <div key={d} className="p-3 text-center" style={{ background: "var(--surface-1)" }}>
              <div className="overline">{DAY_LABELS[i]}</div>
              <div className="font-mono-luxe text-sm mt-1">{d.slice(8)}</div>
            </div>
          ))}
          {MEALS.map((meal) => (
            <>
              <div key={meal} className="p-3 flex items-center" style={{ background: "var(--surface-1)" }}>
                <span className="overline">{meal}</span>
              </div>
              {days.map((d) => {
                const rid = plan.entries[d]?.[meal];
                const r = rid ? recipeById[rid] : null;
                return (
                  <div key={`${d}-${meal}`} className="p-2 min-h-[84px]" style={{ background: "var(--surface-1)" }}>
                    {r ? (
                      <div className="relative group h-full p-2 text-left" style={{ background: "var(--surface-2)" }} data-testid={`plan-slot-${d}-${meal}`}>
                        {r.image_url && <img src={r.image_url} alt="" className="w-full h-10 object-cover mb-1.5" />}
                        <div className="text-[11px] leading-tight font-medium line-clamp-2">{r.title}</div>
                        <button onClick={() => setEntry(d, meal, null)} data-testid={`plan-remove-${d}-${meal}`}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          style={{ background: "var(--surface-1)" }}><X size={11} /></button>
                      </div>
                    ) : (
                      <button onClick={() => setPicker({ date: d, meal })} data-testid={`plan-add-${d}-${meal}`}
                        className="w-full h-full min-h-[68px] flex items-center justify-center transition-colors hover:bg-surface-2"
                        style={{ border: "1px dashed var(--border-strong)", color: "var(--text-tertiary)" }}>
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          ))}
        </div>
      </div>

      <section className="card-luxe max-w-2xl" data-testid="grocery-list">
        <div className="overline flex items-center gap-2"><ShoppingBasket size={13} /> Grocery list · this week</div>
        {grocery.length === 0 ? (
          <p className="text-sm text-secondary-luxe mt-3">Add recipes to your week and your grocery list builds itself.</p>
        ) : (
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {grocery.map(([item, count]) => {
              const checked = plan.checked.includes(item);
              return (
                <li key={item}>
                  <label className="flex items-start gap-3 text-sm cursor-pointer group">
                    <input type="checkbox" checked={checked} onChange={(e) => toggleCheck(item, e.target.checked)}
                      className="mt-0.5 accent-[var(--accent)]" data-testid={`grocery-item-${item.slice(0, 20).replace(/\s+/g, "-")}`} />
                    <span className={checked ? "line-through" : ""} style={{ color: checked ? "var(--text-tertiary)" : "var(--text-secondary)" }}>
                      {item}{count > 1 && <span className="font-mono-luxe text-xs ml-2" style={{ color: "var(--accent)" }}>×{count}</span>}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {picker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,9,8,0.72)" }}
          onClick={() => setPicker(null)} data-testid="recipe-picker">
          <div className="card-luxe max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="overline">Pick a recipe · {picker.meal} · {picker.date.slice(5)}</div>
              <button className="btn-ghost" onClick={() => setPicker(null)}><X size={15} /></button>
            </div>
            <div className="mt-4 space-y-2">
              {recipes.map((r) => (
                <button key={r.recipe_id} data-testid={`pick-recipe-${r.recipe_id}`}
                  onClick={async () => { await setEntry(picker.date, picker.meal, r.recipe_id); setPicker(null); }}
                  className="w-full flex items-center gap-4 p-3 text-left transition-colors hover:bg-surface-2"
                  style={{ border: "1px solid var(--border-strong)" }}>
                  {r.image_url && <img src={r.image_url} alt="" className="w-14 h-14 object-cover flex-shrink-0" />}
                  <div>
                    <div className="text-sm font-medium">{r.title}</div>
                    <div className="text-xs text-secondary-luxe mt-1">{Math.round(r.calories)} cal · {Math.round(r.protein)}g protein · {r.meal_type}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
